/* ==========================================================================
   SHOP BY CATEGORY — carousel controls
   ==========================================================================
   Loaded only when the section's layout_mode is 'carousel'.

   WHAT THIS DOES AND DELIBERATELY DOES NOT DO
   The scroller itself is native CSS (scroll-snap + overflow-x in
   category-carousel.css). Touch dragging, trackpad swiping, momentum,
   keyboard arrow-scrolling and scroll-snap all work with no JavaScript at all,
   so this file failing to load degrades to a still-usable swipe row.

   What a pointer device lacks is a swipe gesture. So this file adds exactly
   two things and nothing more:
     1. prev/next buttons, for mouse and keyboard users;
     2. a progress bar, because the scrollbar is hidden for design reasons and
        the viewer still needs to know how much row is left.

   It does NOT re-implement scrolling, snapping, or lazy loading.
   ========================================================================== */

(function () {
  "use strict";

  var REDUCED = window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

  function motionOK() {
    return !(REDUCED && REDUCED.matches);
  }

  function initCarousel(root) {
    var list = root.querySelector(".collection-grid__list");
    var nav = root.querySelector(".collection-grid__nav");
    if (!list || !nav) return;

    var prev = nav.querySelector('[data-carousel-prev]');
    var next = nav.querySelector('[data-carousel-next]');
    var track = nav.querySelector(".collection-grid__progress");
    var thumb = nav.querySelector(".collection-grid__progress-thumb");

    /* RTL flips the visual meaning of "previous" and "next". The theme ships
       [dir=rtl] rules, so read the direction rather than assume LTR. */
    var rtl =
      (document.documentElement.getAttribute("dir") || "").toLowerCase() ===
      "rtl";

    function overflow() {
      return list.scrollWidth - list.clientWidth;
    }

    /* One "page" is the width of one tile plus one gap, multiplied by how many
       tiles are currently visible. Scrolling by a whole number of tiles is what
       makes each move land exactly on a snap point instead of between two. */
    function step() {
      var first = list.querySelector("li");
      if (!first) return list.clientWidth;
      var cs = getComputedStyle(list);
      var gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      var itemW = first.getBoundingClientRect().width + gap;
      var visible = Math.max(1, Math.round((list.clientWidth + gap) / itemW));
      return itemW * visible;
    }

    function paint() {
      var max = overflow();
      var canScroll = max > 2;

      /* Arrows are shown only when there is somewhere to go. Hiding the whole
         nav on a short row avoids a dead control sitting under the tiles. */
      nav.hidden = !canScroll;
      if (!canScroll) {
        if (prev) prev.disabled = true;
        if (next) next.disabled = true;
        if (thumb) thumb.style.width = "0px";
        return;
      }

      var pos = rtl ? -list.scrollLeft : list.scrollLeft;

      if (prev) prev.disabled = pos <= 1;
      if (next) next.disabled = pos >= max - 1;

      if (track && thumb) {
        var tw = track.clientWidth;
        var frac = list.clientWidth / list.scrollWidth;
        var thumbW = Math.max(24, Math.round(tw * frac));
        var travel = Math.max(0, tw - thumbW);
        var ratio = max > 0 ? Math.min(1, Math.max(0, pos / max)) : 0;
        thumb.style.width = thumbW + "px";
        /* Only transform moves, so this never triggers layout on scroll. */
        thumb.style.transform =
          "translateX(" + Math.round(travel * ratio) + "px)";
      }
    }

    /* Scroll fires far more often than a repaint is useful, so coalesce to one
       paint per frame. */
    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        paint();
      });
    }

    function go(dir) {
      var d = rtl ? -dir : dir;
      var target = list.scrollLeft + d * step();
      var max = overflow();
      if (target < 0) target = 0;
      if (target > max) target = max;
      list.scrollTo({
        left: target,
        behavior: motionOK() ? "smooth" : "auto",
      });
    }

    if (prev) {
      prev.addEventListener("click", function () {
        go(-1);
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        go(1);
      });
    }

    /* touchstart as well as pointerdown: on some touch browsers `pointerdown`
       is retargeted during a drag, so relying on it alone can miss the swipe. */
    list.addEventListener("touchstart", onScroll, { passive: true });
    list.addEventListener("scroll", onScroll, { passive: true });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(onScroll);
      ro.observe(list);
    } else {
      window.addEventListener("resize", onScroll);
    }

    /* Images settle after load and change scrollWidth, so repaint once they
       have, otherwise the progress bar starts out the wrong length. */
    window.addEventListener("load", paint);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(paint).catch(function () {});
    }

    /* The theme editor re-renders a section on every setting change; repaint so
       the controls match the new content. */
    document.addEventListener("shopify:section:load", function (event) {
      if (event.target && event.target.contains(root)) paint();
    });

    paint();
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll(".collection-grid--carousel")
      .forEach(initCarousel);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
